import { ActionIcon, Button, Select, Switch, TextInput, Tooltip } from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { Environment } from '@prisma/client';
import { IconEye, IconEyeOff, IconLock, IconPencil, IconTrash } from '@tabler/icons-react';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useEffect, useMemo, useState } from 'react';
import z from 'zod';
import { ENVIRONMENT_NAMES } from '~/common/environment/names';
import { zVariableValidationType } from '~/common/variable/validation';
import { SENSITIVE_VARIABLE_VALUE } from '~/components/settings/Variables';
import TimeAgo from '~/components/time/TimeAgo';
import { getVariableValue } from '~/server/logic/variable/value';
import { api } from '~/trpc-extra/api';
import type { Variable, VariableForm } from './VariableForm';
import VariableValueInput from '~/components/common/VariableValueInput';

const ICON_SIZE = 16;

export default function VariableRow({
  projectId,
  variable,
  existingVariables,
  environmentScopedEnabled,
}: {
  projectId: string;
  variable: Variable;
  existingVariables: Variable[];
  environmentScopedEnabled: boolean;
}) {
  const trpcUtils = api.useUtils();
  const [revealed, setRevealed] = useState(false);
  const [editting, setEditting] = useState(false);
  const [copied, setCopied] = useState(false);
  const variableValue = useMemo(() => {
    return getVariableValue(variable.value !== undefined ? variable.value : '');
  }, [variable.value]);

  const { data: organizationEmailAddress } = api.stablyEmail.getOrganizationEmailAddress.useQuery({
    projectId,
  });
  const orgPrefix = organizationEmailAddress?.split('@').at(0)?.trim() || '';

  const form = useForm<VariableForm>({
    initialValues: variable,
    validate: zod4Resolver(
      z
        .object({
          name: zVariableValidationType,
          value: z.string().optional(),
          sensitive: z.boolean(),
          environment: z.enum(Environment),
        })
        .superRefine((values, ctx) => {
          if (!values.sensitive && !values.value) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['value'],
              message: 'Value is required',
            });
          }

          // Check for conflicts with other variables, excluding the current one
          const duplicateVariables = existingVariables.filter(
            (existingVariable) =>
              existingVariable.id !== variable.id && // Exclude current variable
              values.name === existingVariable.name &&
              (values.environment === 'ALL' ||
                existingVariable.environment === 'ALL' ||
                values.environment === existingVariable.environment),
          );

          if (duplicateVariables.length > 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['name'],
              message: `There is already a variable with this name${environmentScopedEnabled ? ' in this environment or for all environments' : ''}`,
            });
          }
        }),
    ),
  });

  // Only reset form values when editing state changes or when props change while not editing
  useEffect(() => {
    // use empty string for value to avoid showing the sensitive value
    // value: undefined doesn't work with mantine's form
    if (!editting) {
      form.setValues({ ...variable, value: variableValue });
    }
  }, [editting, variable, variableValue]);

  // If environment scoped is disabled, set environment to ALL (since all vars will be in ALL environment)
  useEffect(() => {
    if (!environmentScopedEnabled) {
      form.setFieldValue('environment', 'ALL');
    }
  }, [environmentScopedEnabled]);

  const { mutate: deleteVariable } = api.variable.delete.useMutation({
    onError: (error) => {
      notifications.show({
        title: 'Error deleting variable',
        message: error.message,
        color: 'red',
      });
    },
    onSuccess: async () => {
      await trpcUtils.variable.list.invalidate();
    },
  });

  const { mutateAsync: updateVariable, isPending: isUpdatingVariable } = api.variable.update.useMutation({
    onError: (error) => {
      notifications.show({
        title: 'Error updating variable',
        message: error.message,
        color: 'red',
      });
    },
    onSuccess: async () => {
      await trpcUtils.variable.list.invalidate();
    },
  });
  return (
    <div
      key={variable.name}
      className="flex flex-col border-b border-gray-200 p-3 dark:border-gray-700"
    >
      <div className="flex items-center gap-4">
        <div className="flex w-2/5 min-w-0 flex-col gap-1">
          <Tooltip label={variable.name}>
            <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{variable.name}</span>
          </Tooltip>
          {!!environmentScopedEnabled && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {ENVIRONMENT_NAMES[variable.environment ?? ('ALL' satisfies Environment)]}
            </span>
          )}
        </div>
        <div className="flex flex-1 items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {variable.sensitive ?
              <Tooltip label="This is a sensitive variable and cannot be revealed">
                <div className="flex h-[22px] w-[22px] items-center justify-center">
                  <IconLock
                    size={ICON_SIZE}
                    className="text-gray-600 dark:text-gray-400"
                  />
                </div>
              </Tooltip>
            : <ActionIcon
                variant="subtle"
                size="lg"
                onClick={() => setRevealed((x) => !x)}
                className="flex items-center hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                {revealed ?
                  <IconEyeOff
                    size={ICON_SIZE}
                    className="text-gray-600 dark:text-gray-400"
                  />
                : <IconEye
                    size={ICON_SIZE}
                    className="text-gray-600 dark:text-gray-400"
                  />
                }
              </ActionIcon>
            }
            <code className="flex min-w-[120px] items-center rounded px-2 py-1 text-sm text-gray-800 dark:text-gray-200">
              {variable.sensitive || !revealed ?
                <span className="font-mono">{SENSITIVE_VARIABLE_VALUE}</span>
              : <Tooltip
                  label={copied ? 'Copied!' : 'Click to copy'}
                  withArrow
                >
                  <span
                    className="cursor-pointer bg-gray-100 font-mono hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700"
                    onClick={() => {
                      void navigator.clipboard.writeText(variableValue);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1000);
                    }}
                  >
                    {variableValue.length > 30 ? `${variableValue.slice(0, 27)}...` : variableValue}
                  </span>
                </Tooltip>
              }
            </code>
          </div>
          <div className="flex items-center gap-2">
            <div className="mr-2 flex items-center gap-1">
              <span className="text-sm text-stone-600 dark:text-stone-400">Updated</span>
              <TimeAgo
                time={variable.updatedAt}
                textStyle="text-stone-600 dark:text-stone-400 text-sm"
              />
            </div>
            <ActionIcon
              variant="subtle"
              onClick={() => {
                setEditting(true);
              }}
              className="flex items-center hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <IconPencil
                size={ICON_SIZE}
                className="text-gray-600 dark:text-gray-400"
              />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              onClick={() => {
                modals.openConfirmModal({
                  title: 'Delete Variable',
                  children: (
                    <p className="text-sm">
                      Are you sure you want to delete this variable? This action cannot be undone.
                    </p>
                  ),
                  labels: { confirm: 'Delete', cancel: 'Cancel' },
                  confirmProps: { color: 'red' },
                  onConfirm: () => {
                    deleteVariable({
                      projectId,
                      name: variable.name || '',
                      environment: variable.environment ?? 'ALL',
                    });
                  },
                });
              }}
              className="flex items-center hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <IconTrash
                size={ICON_SIZE}
                className="text-gray-600 dark:text-gray-400"
              />
            </ActionIcon>
          </div>
        </div>
      </div>
      {!!editting && (
        <div className="flex w-full flex-col gap-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex w-full py-4">
            <form
              className="w-full"
              onSubmit={form.onSubmit(async (values) => {
                const targetEnvironment = environmentScopedEnabled ? (values.environment ?? 'ALL') : 'ALL';

                // For originally sensitive variables, only include value if user modified the field
                // Using isDirty allows setting empty string intentionally (user clears the field)
                const updateData =
                  variable.sensitive ?
                    {
                      name: values.name,
                      sensitive: values.sensitive,
                      environment: targetEnvironment,
                      // Only include value if user modified the value field
                      ...(form.isDirty('value') ? { value: values.value ?? '' } : {}),
                    }
                  : { ...values, environment: targetEnvironment };

                await updateVariable({
                  projectId,
                  name: variable.name || '',
                  environment: variable.environment ?? 'ALL',
                  update: updateData,
                });
                setEditting(false);
              })}
            >
              <div className="flex w-full flex-row items-start gap-4">
                <TextInput
                  className="flex-1"
                  placeholder="Name"
                  {...form.getInputProps('name')}
                />

                <VariableValueInput
                  orgPrefix={orgPrefix}
                  sensitive={form.values.sensitive}
                  {...form.getInputProps('value')}
                />

                <Tooltip
                  withArrow
                  w={220}
                  multiline
                  color="gray"
                  label={
                    variable.sensitive ?
                      'Sensitive variables cannot be changed back to non-sensitive'
                    : 'If enabled, you and your team will not be able to read the value of this variable, or change it back to non-sensitive.'
                  }
                >
                  <div className="pt-1">
                    <Switch
                      label="Sensitive"
                      checked={form.values.sensitive}
                      disabled={variable.sensitive}
                      onChange={(event) => {
                        form.setFieldValue('sensitive', event.currentTarget.checked);
                      }}
                    />
                  </div>
                </Tooltip>
              </div>

              <div className="mt-4 flex w-full flex-row items-center gap-4">
                {!!environmentScopedEnabled && (
                  <Select
                    className="mb-4 w-full"
                    label="Environment"
                    placeholder="Select environment"
                    data={Object.entries(ENVIRONMENT_NAMES)
                      .filter(([environment]) => environment !== 'NONE')
                      .map(([environment, environmentName]) => ({
                        value: environment,
                        label: environmentName,
                      }))}
                    allowDeselect={false}
                    {...form.getInputProps('environment')}
                  />
                )}
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <Button
                  variant="subtle"
                  size="sm"
                  onClick={() => setEditting(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  type="submit"
                  loading={isUpdatingVariable}
                >
                  Save
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
